import { ReactNode, useState, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { redirectService } from "@/services/redirectService";

type ProtectedRouteProps = {
  children: ReactNode;
};

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, isLoading, isSubscriptionPaused } = useAuth();
  const location = useLocation();
  const [shouldRedirect, setShouldRedirect] = useState(false);

  // Add a small buffer before redirecting to prevent flash during auth state transitions
  useEffect(() => {
    let redirectTimer: NodeJS.Timeout;

    if (!isLoading && !user) {
      // Wait a moment before redirecting, in case auth state is still settling
      redirectTimer = setTimeout(() => {
        setShouldRedirect(true);
      }, 500); // Short delay to allow auth state to stabilize
    }

    return () => {
      if (redirectTimer) {
        clearTimeout(redirectTimer);
      }
    };
  }, [user, isLoading]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center space-y-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
          <p className="text-sm text-muted-foreground">Let's go!</p>
        </div>
      </div>
    );
  }

  // Only redirect after the small buffer time has passed
  if (!user && shouldRedirect) {
    // Check if registration is in progress - if so, don't store intended destination
    const isRegistrationInProgress =
      sessionStorage.getItem("registrationInProgress") === "true";

    if (!isRegistrationInProgress) {
      // Store the current location they were trying to go to
      redirectService.setIntendedDestination(location.pathname);
    } else {
      console.log(
        "[ProtectedRoute] Registration in progress, not storing intended destination:",
        location.pathname,
      );
    }

    // Redirect to register
    return <Navigate to="/register" replace />;
  }

  // If we have no user but haven't triggered redirect yet, show loading
  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center space-y-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
          <p className="text-sm text-muted-foreground">Let's go!</p>
        </div>
      </div>
    );
  }

  // Check if subscription is paused and redirect to trial expired page
  // But allow access to trial-expired, payment, and support pages
  const allowedPausedRoutes = ["/trial-expired", "/payment", "/support"];
  const isOnAllowedRoute = allowedPausedRoutes.some((route) =>
    location.pathname.startsWith(route),
  );

  if (isSubscriptionPaused() && !isOnAllowedRoute) {
    return <Navigate to="/trial-expired" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
